import * as React from "react";
import { Maximize2 } from "lucide-react";
import { copyHtmlToClipboard } from "@/helpers/clipboard";
import { CopyActionButton } from "@/components/chat/actions/CopyActionButton";
import { ACTION_BUTTON_STYLE } from "@/components/chat/constants";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useCopyAction } from "@/hooks/useCopyAction";

interface Props {
  children: React.ReactNode;
  hideActions?: boolean;
}

const tableToPlainText = (table: HTMLTableElement): string =>
  Array.from(table.rows)
    .map(row =>
      Array.from(row.cells)
        .map(cell => cell.innerText.trim())
        .join("\t"),
    )
    .join("\n");

export const CopyableTable = ({ children, hideActions }: Props) => {
  const tableRef = React.useRef<HTMLTableElement>(null);
  const { copied, executeCopy } = useCopyAction();
  const [expanded, setExpanded] = React.useState(false);

  const handleCopy = () =>
    executeCopy(async () => {
      if (!tableRef.current) return;
      await copyHtmlToClipboard(tableRef.current.outerHTML, tableToPlainText(tableRef.current));
    });

  // Citations in cells open their source in the side panel, which the modal would otherwise cover.
  // A popover trigger is exempt: closing would unmount the popover before its sources can be picked.
  const closeOnCellButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest("button:not([aria-haspopup])")) setExpanded(false);
  };

  return (
    <div className="group/table relative my-4 w-full min-w-0 max-w-full">
      <div className="w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden rounded-lg border border-desert-sand-300 bg-white/60">
        <table ref={tableRef} className="w-max min-w-full whitespace-nowrap text-sm">
          {children}
        </table>
      </div>
      {!hideActions && (
        <Dialog open={expanded} onOpenChange={setExpanded}>
          <div className="absolute right-2 top-2 flex items-center gap-1 opacity-60 transition-opacity group-focus-within/table:opacity-100 group-hover/table:opacity-100">
            <DialogTrigger asChild>
              <button type="button" className={ACTION_BUTTON_STYLE} aria-label="Expand table" title="Expand table">
                <Maximize2 size={14} />
              </button>
            </DialogTrigger>
            <CopyActionButton copied={copied} onCopy={handleCopy} />
          </div>
          <DialogContent
            aria-describedby={undefined}
            className="flex max-h-[85vh] max-w-[95vw] flex-col overflow-hidden"
          >
            <DialogTitle>Table</DialogTitle>
            <div
              className="min-h-0 overflow-auto rounded-lg border border-desert-sand-300 bg-white/60"
              onClick={closeOnCellButtonClick}
            >
              <table className="min-w-full text-sm">{children}</table>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
